import { Link } from "wouter";
import { useOverview } from "../lib/tenant-api";
import { ErrorBanner, LoadingRow } from "../components/EmptyState";

export default function Overview() {
  const { data, isLoading, error } = useOverview();

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorBanner message={(error as Error).message} />;
  if (!data) return null;

  const cal = data.integrations.google_calendar;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Overview</h1>
        <p className="mt-1 text-sm text-slate-500">
          {data.business?.name} ·{" "}
          {data.business?.is_active ? "active" : "inactive"} ·{" "}
          {data.business?.timezone}
        </p>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="Calls today"
          value={data.today.conversations}
          to="/conversations"
        />
        <Stat
          label="Recent leads"
          value={data.counts.recent_leads}
          to="/leads"
        />
        <Stat
          label="Upcoming bookings"
          value={data.counts.upcoming_bookings}
          to="/bookings"
        />
        <Stat
          label="Pending handoffs"
          value={data.counts.pending_handoffs}
          to="/handoffs"
          highlight={data.counts.pending_handoffs > 0}
        />
      </div>

      {/* Integration status */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-semibold text-slate-900">Integrations</h2>
        <div className="mt-3 space-y-2 text-sm">
          <Row
            label="Google Calendar"
            status={
              cal.connected
                ? `connected${cal.connected_email ? ` as ${cal.connected_email}` : ""}`
                : "not connected"
            }
            ok={cal.connected}
          />
          <Row
            label="Scheduling provider"
            status={data.scheduling_provider}
            ok={data.scheduling_provider !== "none"}
          />
        </div>
        <div className="mt-4">
          <Link
            href="/integrations"
            className="text-sm text-brand-700 hover:underline"
          >
            Manage integrations →
          </Link>
        </div>
      </section>

      {/* Readiness issues — only shown if there are any */}
      {data.readiness_issues.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-semibold text-amber-900">
            ⚠️ {data.readiness_issues.length} configuration issue
            {data.readiness_issues.length === 1 ? "" : "s"}
          </h2>
          <ul className="mt-3 space-y-1 text-sm text-amber-900 list-disc pl-5">
            {data.readiness_issues.map((iss) => (
              <li key={iss}>{iss}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  to,
  highlight,
}: {
  label: string;
  value: number;
  to: string;
  highlight?: boolean;
}) {
  return (
    <Link
      href={to}
      className={`block rounded-xl border bg-white p-4 hover:shadow-sm transition ${
        highlight ? "border-amber-300 bg-amber-50" : "border-slate-200"
      }`}
    >
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{label}</div>
    </Link>
  );
}

function Row({
  label,
  status,
  ok,
}: {
  label: string;
  status: string;
  ok: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-700">{label}</span>
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
        }`}
      >
        {ok ? "✓" : "○"} {status}
      </span>
    </div>
  );
}
